import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Heart, Car, TrendingUp, PiggyBank, ArrowRight,
  CheckCircle, MessageCircle, Star, Users, Phone
} from 'lucide-react';
import useSEO from '../utils/useSEO';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const FinanceLandingPage = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'Finance Services in India | Insurance & Investment Advisory | AGO Innovations',
    description:
      'AGO Innovations provides trusted finance services — life insurance, health insurance, car insurance, investment plans, and mutual fund advisory across India. Get a free consultation today.',
    keywords:
      'insurance services India, life insurance consultant, health insurance plans, car insurance India, investment planning, mutual fund advisor, finance advisory India, insurance advisor near me',
    canonical: 'https://agoinnovations.in/finance',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'FinancialService',
      name: 'AGO Innovations Finance Services',
      provider: { '@type': 'Organization', name: 'AGO Innovations', url: 'https://agoinnovations.in' },
      description:
        'Trusted insurance advisory and investment planning services in India. Life, health, car insurance and mutual fund advisory.',
      areaServed: 'IN',
      serviceType: 'Insurance and Investment Advisory',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Finance Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Life Insurance Advisory' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Health Insurance Plans' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Car Insurance' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Investment Plans' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Mutual Fund Advisory' } },
        ],
      },
    },
  });

  const services = [
    {
      icon: <Shield className="w-7 h-7" />,
      title: 'Life Insurance',
      desc: 'Protect your family\'s financial future with the right term or whole life insurance plan.',
      path: '/finance/life-insurance',
      color: 'from-blue-500 to-blue-700',
      bg: 'bg-blue-50',
      textColor: 'text-blue-700',
    },
    {
      icon: <Heart className="w-7 h-7" />,
      title: 'Health Insurance',
      desc: 'Cashless hospitalisation and medical coverage for you and your entire family.',
      path: '/finance/health-insurance',
      color: 'from-rose-500 to-pink-600',
      bg: 'bg-rose-50',
      textColor: 'text-rose-700',
    },
    {
      icon: <Car className="w-7 h-7" />,
      title: 'Car Insurance',
      desc: 'Third-party and comprehensive vehicle insurance with fast claim support.',
      path: '/finance/car-insurance',
      color: 'from-orange-500 to-amber-600',
      bg: 'bg-orange-50',
      textColor: 'text-orange-700',
    },
    {
      icon: <TrendingUp className="w-7 h-7" />,
      title: 'Investment Plans',
      desc: 'SIPs, PPF, ELSS and more — structured investment plans for every goal.',
      path: '/finance/investment-plans',
      color: 'from-emerald-500 to-teal-600',
      bg: 'bg-emerald-50',
      textColor: 'text-emerald-700',
    },
    {
      icon: <PiggyBank className="w-7 h-7" />,
      title: 'Mutual Funds',
      desc: 'Expert mutual fund advisory — equity, debt, index, and ELSS fund selection.',
      path: '/finance/mutual-funds',
      color: 'from-purple-500 to-indigo-600',
      bg: 'bg-purple-50',
      textColor: 'text-purple-700',
    },
  ];

  const whyUs = [
    { icon: <Star className="w-5 h-5" />, text: 'Trusted advisors with local expertise in Mumbai & Pune' },
    { icon: <CheckCircle className="w-5 h-5" />, text: 'Compare plans from top insurers — LIC, HDFC, ICICI, Bajaj' },
    { icon: <Users className="w-5 h-5" />, text: 'Personalised guidance for individuals and families' },
    { icon: <Shield className="w-5 h-5" />, text: 'No hidden charges — transparent advisory' },
    { icon: <Phone className="w-5 h-5" />, text: 'WhatsApp and email support for quick queries' },
    { icon: <TrendingUp className="w-5 h-5" />, text: 'Investment planning for every budget — ₹500 SIP to ₹50L' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* HERO */}
      <section className="bg-gradient-to-br from-blue-900 via-indigo-900 to-blue-800 pt-28 pb-20 px-4 text-white text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400" />
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative z-10 max-w-4xl mx-auto"
        >
          <span className="inline-block bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 text-sm font-semibold px-5 py-1.5 rounded-full mb-5">
            🛡️ Finance Services — India
          </span>
          <h1 className="text-4xl md:text-6xl font-black mb-5 leading-tight">
            Insurance &amp; Investment
            <span className="block text-yellow-400">Advisory in India</span>
          </h1>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            Get expert guidance on life insurance, health insurance, car insurance, and investment planning.
            Trusted advisors. Transparent advice. Zero pressure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/contact')}
              className="bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              Get Free Consultation <ArrowRight className="w-5 h-5" />
            </motion.button>
            <a
              href="https://wa.me/91XXXXXXXXXX?text=Hi%20AGO%20Innovations!%20I%20need%20insurance%20advisory."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-400 text-white font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" /> WhatsApp Us
            </a>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-blue-200">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Free Consultation</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Compare Top Plans</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> No Hidden Charges</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Mumbai &amp; Pune</span>
          </div>
        </motion.div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
              Our Finance Services
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              From insurance protection to wealth-building investments — we cover every financial need.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                onClick={() => navigate(svc.path)}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer group"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${svc.color} flex items-center justify-center text-white mb-5 shadow-md group-hover:scale-105 transition-transform`}>
                  {svc.icon}
                </div>
                <h3 className="text-gray-900 font-black text-lg mb-2">{svc.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">{svc.desc}</p>
                <span className={`text-sm font-semibold ${svc.textColor} flex items-center gap-1 group-hover:gap-2 transition-all`}>
                  Learn More <ArrowRight className="w-4 h-4" />
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
              Why Choose AGO Innovations for Finance?
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyUs.map((item, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="flex items-start gap-4 bg-blue-50 rounded-xl p-5 border border-blue-100"
              >
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                  {item.icon}
                </div>
                <span className="text-gray-800 font-medium text-sm leading-relaxed">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-800 to-indigo-900 text-white text-center">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-2xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-black mb-4">
            Ready to Secure Your Financial Future?
          </h2>
          <p className="text-blue-200 text-lg mb-8">
            Talk to our advisors for free — no commitment, no pressure. Just honest guidance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              onClick={() => navigate('/contact')}
              className="bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-bold px-8 py-4 rounded-full shadow-lg transition-colors"
            >
              Book Free Consultation
            </motion.button>
            <a
              href="https://wa.me/91XXXXXXXXXX?text=Hi!%20I%20need%20finance%20advisory."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-400 text-white font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" /> WhatsApp Us
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default FinanceLandingPage;
